'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccountError,
  useBlockPart,
  useCommerceSource,
  tx,
  PortalLink,
  type Order,
} from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import { OrderView } from '../commerce/orderViews';
import { errorStyle, mutedStyle, signInHrefFor, titleStyle, useQueryParam, useSignInRedirect } from './accountShared';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * One of the signed-in shopper's own orders, by `?number=`.
 *
 * <p>No email or postcode is asked for: being signed in is the proof. The
 * server answers "not found" for any number that is not this shopper's, so
 * editing the number in the address bar shows nothing of anybody else's.
 */
export interface AccountOrderDetailProps {
  title?: Localized;
  accountHref?: string;
  signInHref?: string;
}

type LoadState = 'loading' | 'found' | 'missing' | 'signed-out' | 'failed';

export function AccountOrderDetail({
  title,
  accountHref = '/account',
  signInHref = '/sign-in',
}: AccountOrderDetailProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const number = useQueryParam('number');

  const [order, setOrder] = useState<Order | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    if (!number.ready || !commerce?.accountOrder) return;
    if (!number.value) {
      setState('missing');
      return;
    }
    let cancelled = false;
    commerce
      .accountOrder(number.value)
      .then((found) => {
        if (cancelled) return;
        setOrder(found);
        setState(found ? 'found' : 'missing');
      })
      .catch((error) => {
        if (cancelled) return;
        setState(error instanceof AccountError && error.kind === 'NOT_SIGNED_IN' ? 'signed-out' : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [commerce, number.ready, number.value]);

  const here =
    typeof window === 'undefined' ? '/account' : `${window.location.pathname}${window.location.search}`;
  useSignInRedirect(state === 'signed-out', signInHref, here);

  if (!commerce?.accountOrder) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="AccountOrderDetail"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 820 }}>
        <PortalLink href={accountHref} style={backStyle}>
          {t('commerce.account.backToAccount')}
        </PortalLink>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.account.orderTitle')}
        </h1>
        {state === 'loading' && <p style={mutedStyle}>{t('commerce.account.loading')}</p>}
        {state === 'found' && order && <OrderView order={order} onChange={setOrder} />}
        {state === 'missing' && (
          <p role="alert" style={errorStyle}>
            {t('commerce.account.errors.NOT_FOUND')}
          </p>
        )}
        {state === 'failed' && (
          <p role="alert" style={errorStyle}>
            {t('commerce.account.errors.UNAVAILABLE')}
          </p>
        )}
        {state === 'signed-out' && (
          <p style={mutedStyle}>
            {t('commerce.account.signedOutRedirecting')}{' '}
            <PortalLink href={signInHrefFor(signInHref, here)}>
              {t('commerce.account.signIn')}
            </PortalLink>
          </p>
        )}
      </div>
    </BlockRoot>
  );
}

const backStyle: CSSProperties = {
  display: 'inline-block',
  marginBottom: 12,
  fontSize: 14,
  color: 'var(--color-text-secondary, #6b7280)',
};
